import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import propertiesService from '../services/propertiesService';
import DateInput from '../components/DateInput';
import styles from '../assets/propertyDocumentEdit.module.css';

const PropertyDocumentEdit = () => {
  const { propertyId, documentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [document, setDocument] = useState(null);
  const [property, setProperty] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    document_type_id: '',
    visible_to_client: false,
    date_completed: '',
    date_expiration: '',
    date_contractor_appointment: ''
  });
  const [errors, setErrors] = useState({});

  // Check if document is a certificate based on type_id
  const isCertificate = () => {
    if (!document) return false;
    const certificateTypeIds = [6, 7, 8, 9, 10, 29, 30, 31, 32, 33];
    return certificateTypeIds.includes(document.document_type_id);
  };

  useEffect(() => {
    loadDocumentData();
  }, [propertyId, documentId]);

  const loadDocumentData = async () => {
    try {
      setLoading(true);
      const response = await propertiesService.editPropertyDocument(propertyId, documentId);
      
      if (response.success) {
        const { document, property, documentTypes } = response.data;
        setDocument(document);
        setProperty(property);
        setDocumentTypes(documentTypes);
        
        // Set form data
        setFormData({
          name: document.name || '',
          document_type_id: document.document_type_id || '',
          visible_to_client: document.visible_to_client || false,
          date_completed: (document.date_completed && document.date_completed !== "Invalid Date") ? document.date_completed : '',
          date_expiration: (document.date_expiration && document.date_expiration !== "Invalid Date") ? document.date_expiration : '',
          date_contractor_appointment: (document.date_contractor_appointment && document.date_contractor_appointment !== "Invalid Date") ? document.date_contractor_appointment : ''
        });
      } else {
        toast.error(response.message || 'Failed to load document data');
        navigate(`/properties/${propertyId}`);
      }
    } catch (error) {
      console.error('Error loading document data:', error);
      toast.error('Failed to load document data');
      navigate(`/properties/${propertyId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.document_type_id) {
      newErrors.document_type_id = 'Document type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD using string manipulation only (no Date objects)
  // This avoids timezone issues where toISOString() shifts dates by converting to UTC
  const toISODateSafe = (dateStr) => {
    if (!dateStr) return null;
    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return dateStr.substring(0, 10);
    const parts = dateStr.split("/");
    if (parts.length !== 3) return dateStr;
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      // Convert dates to YYYY-MM-DD strings to avoid timezone issues
      const submitData = {
        ...formData,
        date_completed: formData.date_completed ? toISODateSafe(formData.date_completed) : null,
        date_expiration: formData.date_expiration ? toISODateSafe(formData.date_expiration) : null,
        date_contractor_appointment: formData.date_contractor_appointment ? toISODateSafe(formData.date_contractor_appointment) : null,
      };
      const response = await propertiesService.updatePropertyDocument(
        propertyId,
        documentId,
        submitData
      );
      
      if (response.success) {
        toast.success('Document updated successfully');
        navigate(`/properties/${propertyId}`);
      } else {
        toast.error(response.message || 'Failed to update document');
      }
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/properties/${propertyId}`);
  };

  if (loading) {
    return (
      <div className="container">
        <div className="panel">
          <div className="panel-body">
            <div className="text-center">
              <p>Loading document data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!document || !property) {
    return (
      <div className="container">
        <div className="panel">
          <div className="panel-body">
            <div className="text-center">
              <p>Document not found</p>
              <button className="btn btn-primary" onClick={handleCancel}>
                Back to Property
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Breadcrumb */}
      <ol className="breadcrumb">
        <li><a href="/">Dashboard</a></li>
        <li><a href="/properties">Properties</a></li>
        <li><a href={`/properties/${propertyId}`}>{property.name}</a></li>
        <li className="active">Edit document</li>
      </ol>

      {/* Main Content */}
      <div className="panel">
        <div className="panel-body">
          <h1 className={styles.pageTitle}>Edit property document</h1>
          
          <form onSubmit={handleSubmit} className={styles.editForm}>
            <div className="row">
              <div className="col-md-6">
                <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
                  <label className="control-label">
                    Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="Enter document name"
                  />
                  {errors.name && (
                    <span className="help-block text-danger">{errors.name}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className={`form-group ${errors.document_type_id ? 'has-error' : ''}`}>
                  <label className="control-label">
                    Document Type <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={document.document_type || 'Unknown'}
                    className="form-control"
                    disabled
                    readOnly
                  />
                  <input
                    type="hidden"
                    name="document_type_id"
                    value={formData.document_type_id}
                  />
                  {errors.document_type_id && (
                    <span className="help-block text-danger">{errors.document_type_id}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Certificate-specific fields - only show for certificates */}
            {isCertificate() && (
              <>
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="control-label">Date Completed</label>
                      <DateInput
                        name="date_completed"
                        value={formData.date_completed}
                        onChange={handleInputChange}
                        placeholder="DD/MM/YYYY"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="control-label">Date Expiration</label>
                      <DateInput
                        name="date_expiration"
                        value={formData.date_expiration}
                        onChange={handleInputChange}
                        placeholder="DD/MM/YYYY"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="control-label">Contractor appointment date</label>
                      <DateInput
                        name="date_contractor_appointment"
                        value={formData.date_contractor_appointment}
                        onChange={handleInputChange}
                        placeholder="DD/MM/YYYY"
                        className="form-control"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <div className="checkbox">
                    <label>
                      <input
                        type="checkbox"
                        name="visible_to_client"
                        checked={formData.visible_to_client}
                        onChange={handleInputChange}
                      />
                      Visible to client
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-default"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save document'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PropertyDocumentEdit;